/** remark plugin that converts `[N]` citation tokens in plain-text mdast
 *  nodes into HTML span placeholders the React renderer can swap for
 *  `SourcesCitation`. The plugin only touches text nodes — citations
 *  inside code blocks are not text-typed in mdast and are left alone
 *  (V1 parity). A hand-rolled recursive walk avoids pulling in
 *  `unist-util-visit`, which is ESM-only and breaks jest in this repo. */

interface MdastNode {
  type: string;
  value?: string;
  children?: MdastNode[];
}

const CITATION_REGEX = /\[(\d+)\]/g;

function buildPlaceholder(citationIndex: string): MdastNode {
  return {
    type: 'html',
    value: `<span data-citation="${citationIndex}"></span>`,
  };
}

function splitTextNode(value: string): MdastNode[] | null {
  if (!value.includes('[')) return null;
  const segments: MdastNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  CITATION_REGEX.lastIndex = 0;
  while ((match = CITATION_REGEX.exec(value)) !== null) {
    // A citation sits against the word it supports, like a footnote mark:
    // the space the model writes before `[N]` is dropped.
    const before = value.slice(lastIndex, match.index).replace(/\s+$/, '');
    if (before.length > 0) {
      segments.push({ type: 'text', value: before });
    }
    const captured = match[1];
    if (captured !== undefined) segments.push(buildPlaceholder(captured));
    lastIndex = match.index + match[0].length;
  }
  if (segments.length === 0) return null;
  if (lastIndex < value.length) {
    segments.push({ type: 'text', value: value.slice(lastIndex) });
  }
  return segments;
}

type ParentLike = MdastNode;

function walk(node: ParentLike): void {
  const children = node.children;
  if (!children) return;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (!child) continue;
    if (child.type === 'text' && typeof child.value === 'string') {
      const replacement = splitTextNode(child.value);
      if (replacement) {
        children.splice(i, 1, ...replacement);
        i += replacement.length - 1;
      }
    } else {
      walk(child);
    }
  }
}

export function remarkCitations() {
  return (tree: MdastNode) => {
    walk(tree);
  };
}
